const { isEmpty } = require('lodash');
const dedent = require('dedent');

const { prefectures, langs, } = require('../config');

const { entries } = Object;

const participantTypes = {
  family: { label: '親子' },
  general: { label: '一般' },
};

const defaultVacantNotificationMailSubject = 'イベントに空きができました {{EVENT_NAME}}';
const defaultVacantNotificationMailBody = dedent`
  {{USER_NAME}}様

  イベント名: {{EVENT_NAME}}
  イベント日: {{LECTURE_DATE}}

  上記イベントへの空きができたこと通知いたします。
  {{ENTRY_URL}}
`;

const defaultLectureFrameImagesMailSubject = '{{EVENT_NAME}} イベント写真閲覧URL';
const defaultLectureFrameImagesMailBody = dedent`
  {{USER_NAME}} 様

  {{EVENT_NAME}} へのご参加ありがとうございました。

  イベント写真の閲覧URLをお送りします。
  以下のURLからご覧いただけます。

  {{IMAGE_URL}}
`;

const defaultImageSurveyReminderMailSubject = '【イベント写真公開のお知らせ】アンケートにご回答ください {{EVENT_NAME}}';
const defaultImageSurveyReminderMailBody = dedent`
  ※ このメールは当イベントに参加申込みされた方のうち、まだアンケートにご回答いただいていない方にお送りしています。

  {{USER_NAME}} 様

  {{EVENT_NAME}} への{{ENTRY_NAME}} さんのご参加ありがとうございました。

  当日のイベント写真の準備が整いました。
  アンケートにご回答いただいた方に、撮影した写真の閲覧URLをお送りしております。

  以下のURLからアンケートにご回答いただくと、折り返し写真の閲覧URLをお送りします。
  ぜひこの機会にご回答ください。

  {{SURVEY_URL}}
`;

const fieldGroups = [
  {
    label: '基本情報',
    fields: ['name', 'lang', 'participantType', 'placeId', 'picId'],
  },
  {
    label: '申込設定',
    fields: ['lectureFrameExpireHours', 'entryIndexPrefix'],
  },
  {
    label: '画面表示',
    fields: ['description', 'followHelpText', 'hasCaution', 'cautionText', 'textForQr'],
  },
  {
    label: 'チェックイン・当日申込',
    fields: [
      'isEnabledCheckIn',
      'checkInDescription',
      'checkInNote',
      'isEnabledCheckInPopup',
      'checkInPopupContent',
      'isEnabledTheDayEntry',
      'theDayQrCodeContent',
    ],
  },
  {
    label: 'メール設定',
    fields: [
      'confirmationMailHeaderMessage',
      'confirmationMailFooterMessage',
      'reminderMailBody',
      'vacantNotificationMailSubject',
      'vacantNotificationMailBody',
      'laterQuestionInstructionMailSubject',
      'laterQuestionInstructionMailBody',
      'lectureFrameImagesMailSubject',
      'lectureFrameImagesMailBody',
      'imageSurveyReminderMailSubject',
      'imageSurveyReminderMailBody',
    ],
  },
  {
    label: '管理用',
    fields: ['isCountIgnored', 'adminNote'],
  },
];

module.exports = {
  defaultVacantNotificationMailSubject,
  defaultVacantNotificationMailBody,
  defaultLectureFrameImagesMailSubject,
  defaultLectureFrameImagesMailBody,
  defaultImageSurveyReminderMailSubject,
  defaultImageSurveyReminderMailBody,
  participantTypes,
  fieldGroups,
  fields: ({ places = [], staffs = [], user = {} } = {}) => {
    return {
      name: {
        label: 'イベント名',
        type: 'string',
        validations: { required:  v => !isEmpty(v), }
      },
      lang: {
        label: '言語',
        type: 'select',
        options: entries(langs).map(([k, v]) => ({ value: k, label: v.label })),
        validations: { required:  v => !isEmpty(v), },
        initialValue: 'ja',
      },
      participantType: {
        label: '参加者種別',
        type: 'select',
        options: entries(participantTypes).map(([k, v]) => ({ value: k, label: v.label })),
        initialValue: 'family',
        validations: { required: v => !isEmpty(v), },
      },
      placeId: {
        label: '開催場所',
        type: 'select',
        options: places.map(({ id, name, prefecture }) => ({ label: `${name} (${prefectures[prefecture]})`, value: id })),
        validations: { required:  v => !isEmpty(v), }
      },
      picId: {
        label: '担当者',
        type: 'select',
        options: staffs.map(({ id, displayName, email }) => ({ label: `${displayName} (${email})`, value: id })),
        initialValue: user.uid,
        validations: { required:  v => !isEmpty(v), },
      },
      lectureFrameExpireHours: {
        label: '申込期限(終了n時間前)',
        type: 'integer',
        initialValue: 0,
        validations: {
          required: v => v != null,
        },
        hint: 'マイナスを入力すると終了後まで申込可能になります（例: -2 → 終了2時間後まで）',
      },
      description: {
        label: '説明文(HTML)',
        type: 'richText',
        rows: 10,
        initialValue: '',
      },
      followHelpText: {
        label: 'フォローヘルプテキスト',
        type: 'richText',
        rows: 3,
        initialValue: '',
        hint: 'フォローボタンの横に表示されるヘルプアイコンをクリックしたときに表示される内容',
      },
      confirmationMailHeaderMessage: {
        label: '申込時メール冒頭部の差込文',
        type: 'text',
        rows: 6,
        initialValue: '',
      },
      confirmationMailFooterMessage: {
        label: '申込時メール末尾部の差込文',
        type: 'text',
        rows: 6,
      },
      reminderMailBody: {
        label: 'リマインダーメール本文',
        type: 'text',
        rows: 10,
        initialValue: dedent`
          イベント名: {{EVENT_NAME}}
          開催日: {{LECTURE_DATE}}
          商品種別: {{LECTURE_PRODUCT_TYPE}}
          レクチャー種別: {{LECTURE_TYPE}}
          対象レベル: {{LECTURE_LEVEL}}
          対象年齢: {{LECTURE_AGE}}
          決済金額: {{LECTURE_PRICE}}
        `
      },
      laterQuestionInstructionMailSubject: {
        label: '事後アンケートメール件名',
        type: 'string',
        initialValue: '{{EVENT_NAME}} への{{ENTRY_NAME}} さんのご参加ありがとうございました',
        hint: '{{EVENT_NAME}}: イベント名, {{ENTRY_NAME}}: 参加者名',
      },
      laterQuestionInstructionMailBody: {
        label: '事後アンケートメール本文',
        type: 'text',
        rows: 10,
        initialValue: dedent`
          ※ このメールは当イベントに参加申込みされたすべての方にお送りしています。

          {{USER_NAME}} 様

          {{EVENT_NAME}} への{{ENTRY_NAME}} さんのご参加ありがとうございました。

          以下のURLからアンケートへのご回答をお願いします。

          {{SURVEY_URL}}
        `,
        hint: '{{USER_NAME}}: ユーザー名, {{ENTRY_NAME}}: 参加者名, {{EVENT_NAME}}: イベント名, {{SURVEY_URL}}: アンケートURL',
      },
      lectureFrameImagesMailSubject: {
        label: 'イベント写真メール件名',
        type: 'string',
        initialValue: defaultLectureFrameImagesMailSubject,
        hint: '{{EVENT_NAME}}: イベント名',
      },
      lectureFrameImagesMailBody: {
        label: 'イベント写真メール本文',
        type: 'text',
        rows: 10,
        initialValue: defaultLectureFrameImagesMailBody,
        hint: '{{USER_NAME}}: ユーザー名, {{EVENT_NAME}}: イベント名, {{IMAGE_URL}}: イベント写真閲覧URL',
      },
      imageSurveyReminderMailSubject: {
        label: '写真公開時アンケート促しメール件名',
        type: 'string',
        initialValue: defaultImageSurveyReminderMailSubject,
        hint: '写真送信開始時に、アンケート未回答者へ送るメールの件名です。{{EVENT_NAME}}: イベント名, {{ENTRY_NAME}}: 参加者名',
      },
      imageSurveyReminderMailBody: {
        label: '写真公開時アンケート促しメール本文',
        type: 'text',
        rows: 10,
        initialValue: defaultImageSurveyReminderMailBody,
        hint: '写真送信開始時に、アンケート未回答者へ送るメールの本文です。{{USER_NAME}}: ユーザー名, {{ENTRY_NAME}}: 参加者名, {{EVENT_NAME}}: イベント名, {{SURVEY_URL}}: アンケートURL',
      },
      isEnabledCheckIn: {
        label: 'チェックイン機能を有効にする',
        type: 'boolean',
        initialValue: false,
      },
      isEnabledTheDayEntry: {
        label: 'チェックイン画面の当日申込みを有効にする',
        type: 'boolean',
        initialValue: false,
      },
      checkInDescription: {
        label: 'チェックイン画面の説明文(HTML)',
        type: 'richText',
        rows: 10,
        initialValue: '',
      },
      checkInNote: {
        label: 'チェックイン画面の備考(HTML)',
        type: 'richText',
        rows: 10,
        initialValue: '',
      },
      isEnabledCheckInPopup: {
        label: 'チェックインコード入力時POPUPを表示',
        type: 'boolean',
        initialValue: false,
        hidden: _ => !_.isEnabledCheckIn,
      },
      checkInPopupContent: {
        label: 'チェックインコード入力時POPUP説明文(HTML)',
        type: 'richText',
        hidden: _ => !_.isEnabledCheckInPopup,
      },
      theDayQrCodeContent: {
        label: '当日申込みQRコード画面下部の説明文(HTML)',
        type: 'richText',
      },
      entryIndexPrefix: {
        label: '申込番号接頭辞',
        type: 'string',
      },
      hasCaution: {
        label: '注意事項を表示する',
        type: 'boolean',
        initialValue: true,
      },
      isCountIgnored: {
        label: 'カウント対象から除外',
        type: 'boolean',
        initialValue: false,
        hint: 'チェックをつけるとイベント一覧画面の「今までに開催されたイベント数」のカウント対象から除外される',
      },
      cautionText: {
        label: '注意事項',
        type: 'richText',
        rows: 10,
        initialValue: '',
        validations: {
          required: v => !isEmpty(v) 
        },
        hidden: _ => !_.hasCaution,
      },
      textForQr: {
        label: 'QRコード表示テキスト',
        type: 'string',
        validations: { required:  v => !isEmpty(v), }
      },
      vacantNotificationMailSubject: {
        label: 'キャンセル待ちメール件名',
        type: 'string',
        initialValue: defaultVacantNotificationMailSubject,
        hint: '{{EVENT_NAME}}: イベント名',
      },
      vacantNotificationMailBody: {
        label: 'キャンセル待ちメール本文',
        type: 'text',
        rows: 10,
        initialValue: defaultVacantNotificationMailBody,
        hint: '{{USER_NAME}}: ユーザー名, {{EVENT_NAME}}: イベント名, {{LECTURE_DATE}}: レクチャー日, {{ENTRY_URL}}: 申込URL',
      },
      adminNote: {
        label: 'メモ',
        type: 'text',
        rows: 3,
        initialValue: '',
      },
    };
  },
  fieldsForInvitedUser: () => {
    return {
      noteForInvitedUser: {
        label: '連絡メモ',
        type: 'richText',
        rows: 10,
        initialValue: '',
      },
    };
  },
};
